// @ts-nocheck
/**
 * Cloud Telephony dashboard registry — modeled after obd3-gui v1's
 * dashboard framework (src/features/dashboard/config.ts). Each widget
 * is a self-contained tile the user can add / remove / drag-reorder /
 * resize via the customizable grid. Tabs scope which widgets are
 * available where. Per-role defaults seed the first visit; thereafter
 * the layout lives in localStorage under `ct3:dashboard:v1:<uid>:<tab>`.
 *
 * Widget-to-tab mapping is derived from the legacy DashboardPage.tsx
 * role gates verbatim — no widget appears on a tab it wasn't already
 * visible to that role before.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertTriangle,
  Ban,
  Bell,
  Building2,
  CheckCircle2,
  ClipboardList,
  FileBarChart,
  FileText,
  Gauge,
  Hash,
  Headphones,
  History,
  Layers,
  ListChecks,
  Megaphone,
  Phone,
  PhoneCall,
  PhoneMissed,
  PieChart,
  Server,
  ShieldCheck,
  TimerReset,
  TrendingUp,
  UserCheck,
  UserCog,
  Users,
  Voicemail,
  Wallet,
} from 'lucide-react';
import { USER_ROLES } from '@/utilities/constant';
import { canReadRecordings } from '@/utilities/recordingsAcl';
import { currentUserCanUseLeads } from '@/utilities/callCentreLeadsAcl';

const {
  SUPERADMIN,
  SUB_SUPERADMIN,
  RESELLER,
  COMPANY_ADMIN,
  CALL_CENTRE_ADMIN,
  CALL_CENTRE_TEAM_LEAD,
} = USER_ROLES;

// Role sets — same shapes used in nav-config.ts.
const ADMIN = [SUPERADMIN, SUB_SUPERADMIN];
const CC_STAFF = [SUPERADMIN, SUB_SUPERADMIN, CALL_CENTRE_ADMIN];
const NON_AGENT = [
  SUPERADMIN,
  SUB_SUPERADMIN,
  RESELLER,
  COMPANY_ADMIN,
  CALL_CENTRE_ADMIN,
  CALL_CENTRE_TEAM_LEAD,
];

export type TabId = 'overview' | 'liveops' | 'agents' | 'reports';
export type LayoutScope = TabId | 'kpi';

export interface DashboardTab {
  id: TabId;
  label: string;
  icon: LucideIcon;
  roles: string[];
}

export const DASHBOARD_TABS: DashboardTab[] = [
  { id: 'overview', label: 'Overview', icon: Gauge, roles: NON_AGENT },
  { id: 'liveops', label: 'Live Ops', icon: Activity, roles: NON_AGENT },
  { id: 'agents', label: 'Agents', icon: Headphones, roles: NON_AGENT },
  { id: 'reports', label: 'Reports', icon: FileBarChart, roles: NON_AGENT },
];

export type WidgetId =
  // KPI strip
  | 'kpi.total-agents'
  | 'kpi.agents-online'
  | 'kpi.agents-on-call'
  | 'kpi.running-campaigns'
  | 'kpi.total-numbers'
  | 'kpi.total-connected'
  | 'kpi.low-balance'
  | 'kpi.licence-alerts'
  | 'kpi.resellers'
  | 'kpi.companies'
  // Overview tab
  | 'overview.users-camp-count'
  | 'overview.disconnected-calls'
  | 'overview.campaign-details'
  | 'overview.calls-charts'
  | 'overview.agent-status-distribution'
  // Live Ops tab
  | 'liveops.cc-admin-alert'
  | 'liveops.system-monitor'
  | 'liveops.cert-expiry'
  | 'liveops.today-running-campaigns'
  | 'liveops.ivr-list'
  // Agents tab
  | 'agents.break-exceeded'
  | 'agents.notification-panel'
  | 'agents.call-stats'
  | 'agents.leads-dashboard'
  | 'agents.pending-dispositions'
  | 'agents.disposition-followups'
  | 'agents.voicemails'
  // Reports tab
  | 'reports.demo-conversion'
  | 'reports.pending-prompts'
  | 'reports.userwise-summary'
  | 'reports.api-users';

export type WidgetSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const SIZE_ORDER: WidgetSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

export const SIZE_DEFS: Record<
  WidgetSize,
  { colsLg: number; colsMd: number; minHeight: number; label: string }
> = {
  xs: { colsLg: 2, colsMd: 3, minHeight: 96, label: 'Tiny' },
  sm: { colsLg: 3, colsMd: 3, minHeight: 130, label: 'Small' },
  md: { colsLg: 6, colsMd: 6, minHeight: 320, label: 'Medium' },
  lg: { colsLg: 8, colsMd: 6, minHeight: 380, label: 'Large' },
  xl: { colsLg: 12, colsMd: 6, minHeight: 460, label: 'Extra large' },
};

export interface WidgetDescriptor {
  id: WidgetId;
  title: string;
  description: string;
  icon: LucideIcon;
  tab: TabId | 'kpi';
  roles: string[];
  defaultSize: WidgetSize;
}

// Role-gated widget registry. Copy the visibility rules from the
// legacy DashboardPage.tsx render-tree verbatim so no role ever sees a
// widget it wasn't seeing before.
export const WIDGETS: WidgetDescriptor[] = [
  /* ── KPI strip ────────────────────────────────────────────── */
  {
    id: 'kpi.total-agents',
    title: 'Total Agents',
    description: 'All configured agents.',
    icon: Users,
    tab: 'kpi',
    roles: NON_AGENT,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.agents-online',
    title: 'Agents Online',
    description: 'Currently logged-in agents.',
    icon: UserCheck,
    tab: 'kpi',
    roles: NON_AGENT,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.agents-on-call',
    title: 'On Call',
    description: 'Agents currently on a call.',
    icon: PhoneCall,
    tab: 'kpi',
    roles: NON_AGENT,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.running-campaigns',
    title: 'Running Campaigns',
    description: 'Campaigns dialing right now.',
    icon: Activity,
    tab: 'kpi',
    roles: NON_AGENT,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.total-numbers',
    title: 'Total Numbers',
    description: 'Numbers uploaded across campaigns.',
    icon: Hash,
    tab: 'kpi',
    roles: NON_AGENT,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.total-connected',
    title: 'Connected',
    description: 'Total calls connected across campaigns.',
    icon: CheckCircle2,
    tab: 'kpi',
    roles: NON_AGENT,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.low-balance',
    title: 'Low Balance',
    description: 'Disconnections due to low balance.',
    icon: Wallet,
    tab: 'kpi',
    roles: CC_STAFF,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.licence-alerts',
    title: 'Licence Alerts',
    description: 'Channel licence exhaustion events.',
    icon: Ban,
    tab: 'kpi',
    roles: CC_STAFF,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.resellers',
    title: 'Resellers',
    description: 'Total resellers on the platform.',
    icon: Users,
    tab: 'kpi',
    roles: ADMIN,
    defaultSize: 'xs',
  },
  {
    id: 'kpi.companies',
    title: 'Companies',
    description: 'Total company admins on the platform.',
    icon: Building2,
    tab: 'kpi',
    roles: ADMIN,
    defaultSize: 'xs',
  },

  // Sizing philosophy — matches Grafana / Retool style for data-heavy
  // operations dashboards:
  //   • data tables → xl (12 cols, full width) so all 5–7 columns fit
  //     without truncation
  //   • charts + hybrids → xl for large widgets, md/lg only when they
  //     naturally pair side-by-side
  //   • metric-chip grids → md (6 cols) — the internal `sm:grid-cols-2`
  //     pattern keeps labels readable
  //   • small alerts → sm (3 cols) so they don't waste width

  /* ── Overview ─────────────────────────────────────────────── */
  {
    id: 'overview.users-camp-count',
    title: 'Users & Campaigns',
    description: 'Aggregate counts of users, campaigns, and calls.',
    icon: Users,
    tab: 'overview',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  {
    id: 'overview.disconnected-calls',
    title: 'Disconnected Calls',
    description: 'Summary of dropped and failed calls.',
    icon: PhoneMissed,
    tab: 'overview',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  {
    id: 'overview.campaign-details',
    title: 'Campaign Details',
    description: 'Snapshot of active campaign health.',
    icon: Megaphone,
    tab: 'overview',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  {
    id: 'overview.calls-charts',
    title: 'Calls Charts',
    description: 'Incoming and outgoing calls charts with date filter.',
    icon: TrendingUp,
    tab: 'overview',
    roles: [SUPERADMIN, SUB_SUPERADMIN, RESELLER, COMPANY_ADMIN, CALL_CENTRE_ADMIN],
    defaultSize: 'xl',
  },
  {
    id: 'overview.agent-status-distribution',
    title: 'Agent Status Distribution',
    description: 'Available / Busy / Offline agent split.',
    icon: PieChart,
    tab: 'overview',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  // Pending Prompts + IVR List moved onto Overview at user request.
  // Kept together in this order so Pending Prompts sits directly above
  // Pending IVRs in the default layout. Widget IDs retain their original
  // 'reports.'/'liveops.' prefix so persisted user layouts still resolve.
  {
    id: 'reports.pending-prompts',
    title: 'Pending Prompts',
    description: 'TTS prompts awaiting moderation.',
    icon: FileText,
    tab: 'overview',
    roles: ADMIN,
    defaultSize: 'xl',
  },
  {
    id: 'liveops.ivr-list',
    title: 'IVR List',
    description: 'Recently updated IVR flows.',
    icon: ListChecks,
    tab: 'overview',
    roles: CC_STAFF,
    defaultSize: 'xl',
  },

  /* ── Live Ops ─────────────────────────────────────────────── */
  {
    id: 'liveops.cc-admin-alert',
    title: 'Call Center Alerts',
    description: 'Active alerts that need attention.',
    icon: AlertTriangle,
    tab: 'liveops',
    roles: [CALL_CENTRE_ADMIN],
    defaultSize: 'sm',
  },
  {
    id: 'liveops.system-monitor',
    title: 'System Monitor',
    description: 'Server + service health at a glance.',
    icon: Server,
    tab: 'liveops',
    roles: ADMIN,
    defaultSize: 'xl',
  },
  {
    id: 'liveops.cert-expiry',
    title: 'Certificate Expiry',
    description: 'TLS/CA certificates approaching expiry.',
    icon: ShieldCheck,
    tab: 'liveops',
    roles: ADMIN,
    defaultSize: 'xl',
  },
  {
    id: 'liveops.today-running-campaigns',
    title: "Today's Running Campaigns",
    description: 'Live campaign feed for today.',
    icon: Layers,
    tab: 'liveops',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  // NOTE: liveops.ivr-list was here — moved to Overview tab above.

  /* ── Agents ───────────────────────────────────────────────── */
  {
    id: 'agents.break-exceeded',
    title: 'Break Exceeded',
    description: 'Agents currently past their break window.',
    icon: TimerReset,
    tab: 'agents',
    roles: CC_STAFF,
    defaultSize: 'xl',
  },
  {
    id: 'agents.notification-panel',
    title: 'Agent Notifications',
    description: 'Assign / manage broadcast messages to agents.',
    icon: Bell,
    tab: 'agents',
    roles: CC_STAFF,
    defaultSize: 'xl',
  },
  {
    id: 'agents.call-stats',
    title: 'Agent Call Stats',
    description: 'Per-agent call summary for the current period.',
    icon: Phone,
    tab: 'agents',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  {
    id: 'agents.leads-dashboard',
    title: 'Agent Leads',
    description: 'Lead pipeline distribution across agents.',
    icon: UserCog,
    tab: 'agents',
    roles: CC_STAFF,
    defaultSize: 'xl',
  },
  {
    id: 'agents.pending-dispositions',
    title: 'Pending Dispositions',
    description: 'Wrap-up forms awaiting agent submission.',
    icon: ClipboardList,
    tab: 'agents',
    roles: CC_STAFF,
    defaultSize: 'xl',
  },
  {
    id: 'agents.disposition-followups',
    title: 'Follow-Ups',
    description: 'Scheduled follow-up calls from dispositions.',
    icon: History,
    tab: 'agents',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  {
    id: 'agents.voicemails',
    title: 'Voicemails',
    description: 'Recent voicemails needing review.',
    icon: Voicemail,
    tab: 'agents',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },

  /* ── Reports ──────────────────────────────────────────────── */
  {
    id: 'reports.demo-conversion',
    title: 'Demo Conversion Analytics',
    description: 'Overall demo-to-conversion analytics.',
    icon: TrendingUp,
    tab: 'reports',
    roles: ADMIN,
    defaultSize: 'xl',
  },
  // NOTE: reports.pending-prompts was here — moved to Overview tab above.
  {
    id: 'reports.userwise-summary',
    title: 'User-wise Campaign Summary',
    description: 'Campaign roll-up per platform user.',
    icon: Users,
    tab: 'reports',
    roles: NON_AGENT,
    defaultSize: 'xl',
  },
  {
    id: 'reports.api-users',
    title: 'API Users',
    description: 'Users of the platform API.',
    icon: FileText,
    tab: 'reports',
    roles: ADMIN,
    defaultSize: 'xl',
  },
];

export interface LayoutItem {
  id: WidgetId;
  size: WidgetSize;
}

/**
 * Non-role visibility gates. The Voicemails & Recordings widget is empty
 * without Recordings `read` ACL (with it, the Recordings tab always shows),
 * so it's hidden entirely then.
 */
export function isWidgetAllowed(id: WidgetId): boolean {
  if (id === 'agents.voicemails') return canReadRecordings();
  if (id === 'agents.leads-dashboard') return currentUserCanUseLeads();
  return true;
}

/**
 * Seed layout for a role + scope. Includes every widget in `WIDGETS`
 * that (a) is scoped to this tab and (b) the role can see. Users can
 * remove tiles in Customize mode; the removal persists per-user.
 */
export function defaultLayoutFor(role: string, scope: LayoutScope): LayoutItem[] {
  return WIDGETS
    .filter((w) => w.tab === scope && w.roles.includes(role) && isWidgetAllowed(w.id))
    .map((w) => ({ id: w.id, size: w.defaultSize }));
}
